import { useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { Truck, Package, Sparkles, Warehouse, ShieldCheck, MapPin, Star, ArrowRight, ArrowUpRight, Phone } from "lucide-react";
import SEO from "@/components/SEO";
import Marquee from "@/components/Marquee";
import GoogleReviews from "@/components/GoogleReviews";
import ChecklistCTA from "@/components/ChecklistCTA";
import Select from "@/components/Select";
import { FadeUp, MaskLine } from "@/components/motion";
import { CONTACT, IMAGES, SERVICES, LOCATIONS, MANIFESTO, SIZE_OPTIONS } from "@/data/content";

const ICONS = { Truck, Package, Sparkles, Warehouse };

const BASE = {
  [SIZE_OPTIONS[0]]: [450, 750],
  [SIZE_OPTIONS[1]]: [890, 1350],
  [SIZE_OPTIONS[2]]: [1290, 1950],
  [SIZE_OPTIONS[3]]: [1890, 2900],
  [SIZE_OPTIONS[4]]: [2200, 4800],
  [SIZE_OPTIONS[5]]: [190, 480],
};

function Estimator() {
  const navigate = useNavigate();
  const [from, setFrom] = useState("Sydney");
  const [to, setTo] = useState("Melbourne");
  const [size, setSize] = useState(SIZE_OPTIONS[1]);
  const [estimate, setEstimate] = useState(null);

  const calc = () => {
    const [lo, hi] = BASE[size] || BASE[SIZE_OPTIONS[1]];
    const inter = from !== to;
    const r = (v) => Math.round(v / 50) * 50;
    setEstimate(
      inter
        ? `$${r(lo * 2.2).toLocaleString()} – $${r(hi * 2.9).toLocaleString()}`
        : `$${r(lo).toLocaleString()} – $${r(hi).toLocaleString()}`
    );
  };

  const sel = "w-full rounded-lg border border-slate-300 bg-white px-4 py-3 text-sm font-semibold text-navy";
  const cities = LOCATIONS.map((l) => l.city);

  return (
    <div data-testid="quick-estimator" className="rounded-2xl border border-white/10 bg-white p-6 sm:p-8 shadow-2xl">
      <p className="text-xs font-mono uppercase tracking-[0.2em] text-amber-600">Instant Estimate</p>
      <h3 className="mt-1 font-display text-xl font-black text-navy">Where are we moving you?</h3>
      <div className="mt-4 grid grid-cols-2 gap-3">
        <Select data-testid="estimator-from" className={sel} value={from} onChange={(e) => setFrom(e.target.value)}>
          {cities.map((c) => <option key={c}>{c}</option>)}
        </Select>
        <Select data-testid="estimator-to" className={sel} value={to} onChange={(e) => setTo(e.target.value)}>
          {cities.map((c) => <option key={c}>{c}</option>)}
        </Select>
      </div>
      <Select data-testid="estimator-size" wrapperClassName="mt-3" className={sel} value={size} onChange={(e) => setSize(e.target.value)}>
        {SIZE_OPTIONS.map((s) => <option key={s}>{s}</option>)}
      </Select>
      <button onClick={calc} data-testid="estimator-calculate-btn"
        className="cta-gleam mt-4 w-full rounded-full bg-navy py-3 text-sm font-bold text-amber-400">
        Calculate Estimate
      </button>
      {estimate && (
        <div data-testid="estimator-result" className="mt-4 rounded-xl bg-amber-50 border border-amber-200 p-4 text-center">
          <p className="text-xs font-semibold uppercase tracking-wider text-slate-500">Indicative range</p>
          <p className="font-display text-2xl font-black text-navy">{estimate}</p>
          <p className="mt-1 text-[11px] text-slate-500">Guide only, your full quote itemises everything in writing.</p>
          <button
            onClick={() => navigate(`/quote?from=${encodeURIComponent(from)}&to=${encodeURIComponent(to)}&size=${encodeURIComponent(size)}`)}
            data-testid="estimator-to-quote-btn"
            className="mt-3 inline-flex items-center gap-1 text-sm font-bold text-amber-700 hover:text-amber-800">
            Lock in a detailed quote <ArrowRight className="h-4 w-4" />
          </button>
        </div>
      )}
    </div>
  );
}

export default function Home() {
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "20%"]);

  return (
    <main>
      <SEO
        title="Local & Interstate Removalists"
        description="NextMove Removalists: Australia's trusted interstate and local removalists. Reliable, fully-insured residential & commercial moves, packing, and bond cleaning. Get an instant quote today."
        path="/"
      />

      {/* Kinetic hero */}
      <section ref={heroRef} data-testid="hero-section" className="relative overflow-hidden bg-navy grain pt-20">
        <div className="mx-auto grid max-w-7xl gap-12 px-4 sm:px-8 pb-20 pt-14 sm:pt-20 lg:grid-cols-[1.2fr_1fr] lg:items-center">
          <div>
            <FadeUp delay={0.1}>
              <p className="text-xs font-mono uppercase tracking-[0.22em] font-semibold text-amber-500">
                Australia-wide relocation specialists · Residential &amp; Commercial
              </p>
            </FadeUp>
            <h1 className="mt-6 font-display text-4xl sm:text-5xl lg:text-6xl font-black tracking-tight leading-[1.08] text-white">
              <MaskLine delay={0.2}>Moves without</MaskLine>
              <MaskLine delay={0.32}>the <span className="text-amber-400">mayhem.</span></MaskLine>
              <MaskLine delay={0.44}>Handled to perfection.</MaskLine>
            </h1>
            <FadeUp delay={0.6}>
              <p className="mt-6 max-w-xl text-base sm:text-lg leading-relaxed text-slate-300">
                Tracked fleets, transparent cubic-metre rates, and every charge itemised in writing
                before move day. Tell us where you're headed, we'll handle the rest.
              </p>
            </FadeUp>
            <FadeUp delay={0.75} className="mt-8 flex flex-wrap gap-3">
              <Link to="/quote" data-testid="hero-quote-btn"
                className="cta-gleam inline-flex items-center gap-2 rounded-full bg-amber-500 px-7 py-3.5 text-sm font-bold text-navy">
                Get a Fast Quote <ArrowUpRight className="h-4 w-4" />
              </Link>
              <a href={`tel:${CONTACT.phonePrimaryRaw}`} data-testid="hero-call-btn"
                className="inline-flex items-center gap-2 rounded-full border border-white/25 px-7 py-3.5 text-sm font-bold text-white hover:border-amber-400 hover:text-amber-400 transition-colors duration-200">
                <Phone className="h-4 w-4" /> {CONTACT.phonePrimary}
              </a>
            </FadeUp>
            <FadeUp delay={0.9} className="mt-10 flex flex-wrap gap-x-6 gap-y-2 text-xs font-semibold text-slate-300">
              <span className="flex items-center gap-1.5"><Star className="h-4 w-4 text-amber-400" /> 5-star rated on Google</span>
              <span className="flex items-center gap-1.5"><ShieldCheck className="h-4 w-4 text-amber-400" /> Fully insured operators</span>
              <span className="flex items-center gap-1.5"><MapPin className="h-4 w-4 text-amber-400" /> ABN {CONTACT.abn}</span>
            </FadeUp>
          </div>

          <FadeUp delay={0.5} className="relative">
            <div className="relative overflow-hidden rounded-3xl">
              <motion.img
                src={IMAGES.hero}
                alt="NextMove crew unloading a wrapped sofa from a branded truck at a customer's home"
                style={{ y: imgY }}
                className="h-64 sm:h-80 w-full scale-110 object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-navy/70 to-transparent" />
            </div>
            <div className="relative z-10 -mt-16 mx-4 sm:mx-8">
              <Estimator />
            </div>
          </FadeUp>
        </div>
      </section>

      <Marquee />

      {/* Services */}
      <section data-testid="services-section" className="mx-auto max-w-7xl px-4 sm:px-8 py-20 sm:py-28">
        <FadeUp>
          <p className="text-xs font-mono uppercase tracking-[0.22em] font-semibold text-amber-600">What we do</p>
          <h2 className="mt-3 font-display text-3xl sm:text-4xl font-extrabold tracking-tight text-navy">
            Four services. One accountable crew.
          </h2>
        </FadeUp>
        <div className="mt-12 grid gap-6 sm:grid-cols-2">
          {SERVICES.map((s, i) => {
            const Icon = ICONS[s.icon];
            return (
              <FadeUp key={s.slug} delay={i * 0.08}>
                <Link to={`/services/${s.slug}`} data-testid={`service-card-${s.slug}`}
                  className="card-lift group flex h-full flex-col justify-between rounded-2xl border border-slate-200 bg-white p-8 sm:p-10">
                  <div>
                    <span className="flex h-12 w-12 items-center justify-center rounded-xl bg-navy text-amber-400">
                      <Icon className="h-6 w-6" />
                    </span>
                    <h3 className="mt-6 font-display text-xl sm:text-2xl font-bold tracking-tight text-navy">{s.title}</h3>
                    <p className="mt-2 text-sm sm:text-base leading-relaxed text-slate-600">{s.tagline}</p>
                  </div>
                  <span className="mt-8 inline-flex items-center gap-1 text-sm font-bold text-amber-600">
                    Explore {s.name}
                    <ArrowRight className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-1" />
                  </span>
                </Link>
              </FadeUp>
            );
          })}
        </div>
      </section>

      {/* Manifesto */}
      <section data-testid="manifesto-section" className="bg-slate-100 py-20 sm:py-28">
        <div className="mx-auto max-w-7xl px-4 sm:px-8">
          <FadeUp>
            <p className="text-xs font-mono uppercase tracking-[0.22em] font-semibold text-amber-600">The NextMove standard</p>
            <h2 className="mt-3 max-w-2xl font-display text-3xl sm:text-4xl font-extrabold tracking-tight text-navy">
              Our manifesto for a move done right.
            </h2>
          </FadeUp>
          <div className="mt-12 grid gap-px overflow-hidden rounded-2xl border border-slate-200 bg-slate-200 sm:grid-cols-2 lg:grid-cols-4">
            {MANIFESTO.map((m, i) => (
              <FadeUp key={m.num} delay={i * 0.08} className="bg-white p-8 sm:p-10">
                <p className="font-display text-4xl sm:text-5xl font-black tracking-tighter text-amber-500">{m.num}</p>
                <h3 className="mt-4 font-display text-lg font-bold text-navy">{m.title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-slate-600">{m.desc}</p>
              </FadeUp>
            ))}
          </div>
        </div>
      </section>

      {/* Fleet statement band */}
      <section className="relative h-[380px] overflow-hidden">
        <img src={IMAGES.fleet} alt="NextMove interstate linehaul truck" className="h-full w-full object-cover" />
        <div className="absolute inset-0 bg-navy/70" />
        <div className="absolute inset-0 flex items-center justify-center px-4">
          <FadeUp>
            <p className="max-w-3xl text-center font-display text-2xl sm:text-4xl font-black tracking-tight text-white">
              Daily linehaul between every Australian capital.
              <span className="text-amber-400"> Your load, tracked the whole way.</span>
            </p>
          </FadeUp>
        </div>
      </section>

      {/* Areas strip */}
      <section data-testid="areas-strip" className="mx-auto max-w-7xl px-4 sm:px-8 py-20 sm:py-28">
        <FadeUp className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <p className="text-xs font-mono uppercase tracking-[0.22em] font-semibold text-amber-600">Coverage</p>
            <h2 className="mt-3 font-display text-3xl sm:text-4xl font-extrabold tracking-tight text-navy">Areas we service</h2>
          </div>
          <Link to="/areas" data-testid="areas-strip-all-link" className="text-sm font-bold text-amber-600 hover:text-amber-700">
            View all areas →
          </Link>
        </FadeUp>
        <div className="mt-10 flex flex-wrap gap-3">
          {LOCATIONS.map((l, i) => (
            <FadeUp key={l.slug} delay={i * 0.03}>
              <Link to={`/areas/${l.slug}`} data-testid={`area-chip-${l.slug}`}
                className="card-lift inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white px-5 py-2.5 text-sm font-semibold text-navy">
                <MapPin className="h-4 w-4 text-amber-500" /> {l.city}, {l.state}
              </Link>
            </FadeUp>
          ))}
        </div>
      </section>

      {/* Testimonials */}
      <section data-testid="testimonials-section" className="bg-navy grain py-20 sm:py-28">
        <div className="mx-auto max-w-7xl px-4 sm:px-8">
          <GoogleReviews />
        </div>
      </section>

      {/* Checklist lead magnet */}
      <ChecklistCTA />

      {/* CTA banner */}
      <section data-testid="cta-banner" className="mx-auto max-w-7xl px-4 sm:px-8 py-20 sm:py-28">
        <FadeUp className="relative overflow-hidden rounded-3xl bg-navy grain px-8 py-16 sm:px-16 text-center">
          <h2 className="font-display text-3xl sm:text-5xl font-black tracking-tight text-white">
            Ready when you are.
          </h2>
          <p className="mx-auto mt-4 max-w-xl text-slate-300">
            Two minutes on the form, and an itemised written quote lands in your inbox.
          </p>
          <div className="mt-8 flex flex-wrap justify-center gap-3">
            <Link to="/quote" data-testid="cta-quote-btn"
              className="cta-gleam inline-flex items-center gap-2 rounded-full bg-amber-500 px-8 py-4 text-sm font-bold text-navy">
              Get a Fast Quote <ArrowUpRight className="h-4 w-4" />
            </Link>
            <a href={`tel:${CONTACT.phoneMobileRaw}`} data-testid="cta-call-btn"
              className="inline-flex items-center gap-2 rounded-full border border-white/25 px-8 py-4 text-sm font-bold text-white hover:border-amber-400 hover:text-amber-400 transition-colors duration-200">
              <Phone className="h-4 w-4" /> {CONTACT.phoneMobile}
            </a>
          </div>
        </FadeUp>
      </section>
    </main>
  );
}
